import { Injectable, NgZone } from '@angular/core';
import { LoggerFactory, UniversalLoaderProvider } from '@valhalla/core';
import { firstLetterLowerCaseMapper, jsonTryParse, rxSetInterval, trimProps } from '@valhalla/utils';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, share, tap } from 'rxjs/operators';
import { TabActiveStateService } from '@spa/common/services/tab-active-state.service';

import {
	IAddTaskToFavorites,
	IChatMessageNotifyData,
	IEditCommentNotifyData,
	IEditTaskTextNotifyData,
	IJitsiRoomJoin,
	INewCommentNotifyData,
	IQuestionCommentNotifyData,
	ISignalMessage,
	ISmartMessage,
	NotifyHubEvents,
	ReadCommentsNotifyData,
	SignalrProvider,
} from './abstract';

@Injectable()
export class SignalrProviderImpl implements SignalrProvider {
	constructor(
		readonly scriptLoader: UniversalLoaderProvider,
		readonly loggerFactory: LoggerFactory,
		readonly zone: NgZone,
		readonly tab: TabActiveStateService
	) {}

	private _messages$ = new Subject<ISignalMessage>();
	private _isActivated: boolean;
	private _isConnectToNotificationHub = false;
	private _logger = this.loggerFactory.createLogger('SignalrProvider');
	readonly connectionState = {
		connecting: 0,
		connected: 1,
		reconnecting: 2,
		disconnected: 4,
	};
	readonly signal$ = this._messages$.asObservable();
	readonly messages$: Observable<ISignalMessage> = this._messages$.pipe(
		filter(_ => this._isActivated),
		filter(message => this.filterSignalByTabActive(message)),
		share()
	);
	readonly chatMessages$: Observable<IChatMessageNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.chatMessage),
		map((event: ISignalMessage<IChatMessageNotifyData>) => {
			event.data = trimProps(event.data, firstLetterLowerCaseMapper);
			event.data.__isNormalizeProps = true;
			return event.data;
		}),
		share()
	);

	readonly editTaskText$: Observable<IEditTaskTextNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.editTaskText),
		map((event: ISignalMessage<IEditTaskTextNotifyData>) => event.data),
		share()
	);
	readonly readComments$: Observable<ReadCommentsNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.readComments),
		map((event: ISignalMessage<ReadCommentsNotifyData>) => event.data),
		share()
	);
	readonly newComments$: Observable<INewCommentNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.newComment),
		map((event: ISignalMessage<INewCommentNotifyData>) => event.data),
		share()
	);
	readonly questionComment$: Observable<IQuestionCommentNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.questionComment),
		map((event: ISignalMessage<IQuestionCommentNotifyData>) => event.data),
		share()
	);
	readonly updateFavorites$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.updateFavorites),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);
	readonly refreshMTF$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.refreshMTF),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);
	readonly deleteComment$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.deleteComment),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly editComment$: Observable<IEditCommentNotifyData> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.editComment),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly likeComment$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.likeComment),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly userProfileChanged$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.userProfileChanged),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly newChatComment$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.newChatComment),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly impersonate$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.impersonate),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly stopImpersonation$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.stopImpersonation),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly changeLanguage$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.changeLanguage),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly addTaskToFavorites$: Observable<IAddTaskToFavorites> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.addTaskToFavorites),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly changeFavorites$: Observable<any> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.changeFavorites),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	readonly jitsiRoomJoin$: Observable<IJitsiRoomJoin> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.jitsiRoomJoin || event.name === NotifyHubEvents.conferenceRoomJoin),
		map((event: ISignalMessage<IJitsiRoomJoin>) => event.data),
		share()
	);

	readonly smartMessage$: Observable<ISmartMessage> = this.messages$.pipe(
		filter(event => event.name === NotifyHubEvents.smartMessage),
		map((event: ISignalMessage<any>) => event.data),
		share()
	);

	get signalRef() {
		// todo: make typed
		try {
			return (<any>window).SJ?.iwc?.SignalR || (<any>window).$.signalR;
		} catch (err) {
			this._logger.error(
				`${String(err)}, signalR bundles not loaded correctly, can't access to global property window.SJ.iwc.SignalR!`
			);
			throw err;
		}
	}

	activate(): Observable<boolean> {
		if (localStorage.getItem('no-signalr')) {
			return of(true);
		}
		if (this._isActivated !== undefined) {
			this._isActivated = true;
			return of(true);
		}
		return this.scriptLoader.loadSignalR().pipe(
			tap(isLoaded => {
				if (isLoaded) {
					this.connectToNotificationHub();
					this.activateIntervalCheckConnectionState();
					this._isActivated = true;
				} else {
					// tslint:disable-next-line:no-console
					console.warn('SignalrProvider: signalR scripts is not loaded!');
				}
			}),
			catchError(err => {
				console.error(err);
				return EMPTY;
			})
		);
	}
	deactivate(): Observable<any> {
		this._isActivated = false;
		return of(true);
	}
	getState(): any {
		return;
	}

	connectToNotificationHub() {
		try {
			const hubName = 'notificationHub';
			if (this.signalRef.start && this.signalRef.getHubProxy) {
				this.signalRef.start();
				this.signalRef.getHubProxy(hubName, {
					client: {
						notify: (event: string, data: string) => {
							this._logger.log(hubName, event, data);
							this._messages$.next({
								name: event,
								data: data && jsonTryParse(data, err => console.error(`${hubName}/${event}`, err, data)),
							});
						},
					},
				});
				this._isConnectToNotificationHub = true;
			} else if (this.signalRef.notificationHub?.client) {
				this.signalRef.notificationHub.client.notify = (event: string, data: string) => {
					this._logger.log(hubName, event, data);
					this._messages$.next({
						name: event,
						data: data && jsonTryParse(data, err => console.error(`${hubName}/${event}`, err, data)),
					});
				};
				this.signalRef.hub.start();
				this._isConnectToNotificationHub = true;
			}
		} catch (err) {
			this._logger.error(`Error connect to notification hub.\n${String(err)}`);
		}
	}

	activateIntervalCheckConnectionState() {
		this.zone.runOutsideAngular(() => {
			rxSetInterval(30000).subscribe(_ => {
				try {
					// check connection state and reconnect
					if (!this._isConnectToNotificationHub) {
						this.connectToNotificationHub();
					}
					const connection = (<any>window).$ && (<any>window).$.connection;
					if (!connection) {
						return;
					}
					if (connection.hub.state === this.connectionState.disconnected) {
						this._logger.info('SignalR seems is disconnected, trying connect again...');
						this.zone.run(() => {
							try {
								connection.hub.start();
							} catch (err) {
								this._logger.error(
									`Error in interval checking signalR connection. Start connection hub error.\n${String(err)}`
								);
							}
						});
					}
				} catch (err) {
					this._logger.error(`Error in interval checking signalR connection.\n${String(err)}`);
				}
			});
		});
	}

	filterSignalByTabActive(message: ISignalMessage<any>) {
		const allTabsSignal: string[] = [
			NotifyHubEvents.jitsiRoomJoin,
			NotifyHubEvents.jitsiRoomUserJoined,
			NotifyHubEvents.jitsiRoomInviteDeclined,
			NotifyHubEvents.conferenceRoomJoin,
		];
		if (allTabsSignal.includes(message.name)) {
			return true;
		}
		return this.tab.tabVisible;
	}
}
